// iter v28 r295 — Modal "Analisi annuncio" con score 0-100 e 5 punti azionabili.
import { useEffect, useState } from "react";
import { X, CheckCircle, Warning, Info, ArrowRight, Spinner, Sparkle } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const SEVERITY_STYLE = {
  critical: { bg: "bg-rose-50", border: "border-rose-300", icon: Warning, color: "text-rose-600" },
  warning:  { bg: "bg-amber-50", border: "border-amber-300", icon: Warning, color: "text-amber-600" },
  info:     { bg: "bg-blue-50", border: "border-blue-300", icon: Info, color: "text-blue-600" },
  good:     { bg: "bg-emerald-50", border: "border-emerald-300", icon: CheckCircle, color: "text-emerald-600" },
};

export default function ListingAnalysisModal({ propertyId, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!propertyId) return;
    let alive = true;
    (async () => {
      try {
        const { data: r } = await api.get(`/properties/${propertyId}/analysis`);
        if (alive) setData(r);
      } catch (e) {
        toast.error(apiError(e, "Errore analisi annuncio"));
        if (alive) onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [propertyId, onClose]);

  if (!propertyId) return null;

  const scoreColor = (s) =>
    s >= 85 ? "text-emerald-500" : s >= 70 ? "text-[#B58A4E]" : s >= 50 ? "text-amber-500" : "text-rose-500";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="listing-analysis-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#EDEAE2] flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-[#1C1C1A] text-white flex items-center justify-center">
            <Sparkle size={18} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E]">
              Analisi annuncio
            </div>
            <h3 className="font-editorial text-xl text-[#1C1C1A] mt-0.5">
              Come si presenta al pubblico
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[#9A968B] hover:text-[#1C1C1A] p-1 rounded-full hover:bg-[#F5F4F0]"
            aria-label="Chiudi"
            data-testid="listing-analysis-close"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex flex-col items-center justify-center gap-3 text-[#9A968B]">
            <Spinner size={28} className="animate-spin" />
            <div className="text-[13px]">Analizzo l'annuncio…</div>
          </div>
        ) : data ? (
          <>
            {/* Score hero */}
            <div className="p-6 text-center border-b border-[#EDEAE2]" data-testid="analysis-score">
              <div className={`font-editorial text-7xl tabular-nums leading-none ${scoreColor(data.score)}`}>
                {data.score}
              </div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#9A968B] mt-1">
                / 100 · {data.grade}
              </div>
              <div className="text-[12px] text-[#6B6A65] mt-3 flex items-center justify-center gap-3 flex-wrap">
                {data.summary.critical > 0 && (
                  <span className="inline-flex items-center gap-1 text-rose-600">
                    <Warning size={13} weight="fill" /> {data.summary.critical} critic{data.summary.critical === 1 ? "o" : "i"}
                  </span>
                )}
                {data.summary.warning > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-600">
                    <Warning size={13} weight="regular" /> {data.summary.warning} da sistemare
                  </span>
                )}
                {data.summary.info > 0 && (
                  <span className="inline-flex items-center gap-1 text-blue-600">
                    <Info size={13} weight="regular" /> {data.summary.info} suggerimento
                  </span>
                )}
                {data.summary.good > 0 && (
                  <span className="inline-flex items-center gap-1 text-emerald-600">
                    <CheckCircle size={13} weight="fill" /> {data.summary.good} OK
                  </span>
                )}
              </div>
            </div>

            {/* Findings list */}
            <div className="p-5 space-y-2.5">
              {data.findings.map((f, i) => {
                const s = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.info;
                const Icon = s.icon;
                return (
                  <div
                    key={i}
                    className={`${s.bg} ${s.border} border rounded-lg p-3 flex items-start gap-2.5`}
                    data-testid={`analysis-finding-${f.kind}`}
                  >
                    <Icon size={17} weight="fill" className={`${s.color} shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#1C1C1A] leading-tight">
                        {f.title}
                      </div>
                      <div className="text-[12px] text-[#3F3D38] leading-snug mt-0.5">
                        {f.fix}
                      </div>
                      {f.action_url && (
                        <button
                          onClick={() => { onClose(); navigate(f.action_url); }}
                          className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#1C1C1A] hover:underline"
                          data-testid={`analysis-action-${f.kind}`}
                        >
                          Sistema ora <ArrowRight size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-5 pb-5 text-[11px] text-[#9A968B] italic text-center">
              Analisi automatica · Pronto controlla foto, descrizione, titolo e prezzo vs zona.
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
